import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Popover, PopoverModule } from 'primeng/popover';
import { ToastModule } from 'primeng/toast';

import { AppNotification, NotificationType } from '@/model/availability.model';
import { NotificationsService } from '@/services/notifications.service';
import { JalaliDatePipe } from '@/pipes/jalali-date.pipe';

const SEVERITY: Record<NotificationType, 'success' | 'info' | 'warn' | 'error' | 'secondary'> = {
  public_reservation_created: 'success',
  reservation_created: 'info',
  status_changed: 'info',
  starting_soon: 'warn',
  ending_soon: 'warn',
  block_conflict: 'error',
  payment_received: 'success',
  reservation_expired: 'secondary',
};

const ICONS: Record<NotificationType, string> = {
  public_reservation_created: 'pi pi-globe',
  reservation_created: 'pi pi-calendar-plus',
  status_changed: 'pi pi-sync',
  starting_soon: 'pi pi-hourglass',
  ending_soon: 'pi pi-stopwatch',
  block_conflict: 'pi pi-exclamation-triangle',
  payment_received: 'pi pi-credit-card',
  reservation_expired: 'pi pi-clock',
};

// زنگوله‌ی اعلان‌ها در نوار بالا + پیام‌های نمایشی هشدار
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule, BadgeModule, ButtonModule, OverlayBadgeModule, PopoverModule, ToastModule, JalaliDatePipe],
  providers: [MessageService],
  template: `
    <button type="button" class="layout-topbar-action" (click)="toggle($event)" aria-label="اعلان‌ها">
      @if (service.unreadCount() > 0) {
        <p-overlaybadge [value]="badge()" severity="danger">
          <i class="pi pi-bell"></i>
        </p-overlaybadge>
      } @else {
        <i class="pi pi-bell"></i>
      }
    </button>

    <p-popover #panel styleClass="notification-panel">
      <div class="w-[22rem] max-w-[90vw]">
        <div class="flex items-center justify-between gap-2 mb-3">
          <span class="font-bold">اعلان‌ها</span>
          <div class="flex items-center gap-3">
            <button *ngIf="service.visible().length" type="button" class="text-sm text-primary" (click)="service.dismissAll()">
              <i class="pi pi-check-square ml-1"></i>همه خوانده شد
            </button>
            <a routerLink="/alerts" class="text-sm text-surface-500" (click)="panel.hide()" title="تنظیم هشدارها"><i class="pi pi-cog"></i></a>
          </div>
        </div>
        <div *ngIf="!service.visible().length" class="text-surface-500 text-center py-6">اعلان خوانده‌نشده‌ای وجود ندارد</div>
        <div class="max-h-[60vh] overflow-auto flex flex-col gap-1">
          <div
            *ngFor="let n of service.visible()"
            class="flex gap-3 p-2 rounded"
            [class.bg-primary-50]="n.id > service.lastSeenId()"
            [class.dark:bg-surface-800]="n.id > service.lastSeenId()"
          >
            <i [class]="icon(n) + ' mt-1'"></i>
            <div class="flex-1 min-w-0">
              <div class="font-bold text-sm">{{ n.title }}</div>
              <div class="text-sm text-surface-600 dark:text-surface-300 break-words">{{ n.message }}</div>
              <div class="text-xs text-surface-400 mt-1">{{ n.createdAt | jalaliDate: 'HH:mm - YYYY/MM/DD' }}</div>
            </div>
            <button type="button" class="notif-read" (click)="service.dismiss(n.id)" title="خوانده شد" aria-label="خوانده شد">
              <i class="pi pi-check"></i>
            </button>
          </div>
        </div>
      </div>
    </p-popover>

    <p-toast key="alerts" position="top-left" [preventOpenDuplicates]="true" />

    <style>
      .notif-read {
        align-self: flex-start;
        width: 1.8rem;
        height: 1.8rem;
        border-radius: 50%;
        border: 1px solid var(--surface-border);
        background: var(--surface-card);
        color: var(--text-color-secondary);
        cursor: pointer;
        flex-shrink: 0;
      }
      .notif-read:hover {
        color: var(--primary-color);
        border-color: var(--primary-color);
      }
    </style>
  `,
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @ViewChild('panel') panel!: Popover;
  private sub?: Subscription;

  constructor(
    public service: NotificationsService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.service.start();
    this.sub = this.service.alert$.subscribe((n) => this.show(n));
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.service.stop();
  }

  badge() {
    const count = this.service.unreadCount();
    return count > 9 ? '9+' : String(count);
  }

  icon(n: AppNotification) {
    return ICONS[n.type] ?? 'pi pi-bell';
  }

  toggle(event: Event) {
    this.panel.toggle(event);
    // باز کردن فهرست یعنی همه دیده شده‌اند
    this.service.markAllRead();
  }

  private show(n: AppNotification) {
    const sticky = n.type === 'block_conflict' || n.type === 'starting_soon';
    this.messageService.add({
      key: 'alerts',
      severity: SEVERITY[n.type],
      summary: n.title,
      detail: n.message,
      life: 10_000,
      sticky,
    });
  }
}
